import 'swiper/swiper-bundle.css';

import { Button, Icon, Tooltip, Typography } from '@smartgift/frontend-toolkit';
import { maxBy, minBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Notification } from '@hero/atoms';
import { useCreatePriceLabel } from '@hero/hooks';
import { ShowMore } from '@hero/molecules';
import { getFormattedPrice, getSafeInnerHtml } from '@hero/utils';

import { HorizontalSkuCard } from './HorizontalSkuCard';
import { ProductDetailImage } from './ProductDetailImage';
import { ProductDetailInfo } from './ProductDetailInfo';
import { ProductOptionsByCheckbox } from './ProductOptionsByCheckbox';
import * as S from './style';
import { TopNotifications } from './TopNotifications';

export default function ProductDetailByCheckbox({
  product,
  selectedSkus,
  setSelectedSkus,
  someSkusUnavailable,
  isDraft
}) {
  const { t } = useTranslation();

  const navigationNextRef = useRef(null);
  const navigationPrevRef = useRef(null);

  const [hideVerticalBasket, setHideVerticalBasket] = useState(false);

  const { isCAActMustBeApplied } = useCreatePriceLabel(product);

  const minSkuPrice = isCAActMustBeApplied
    ? maxBy(selectedSkus, 'priceBreakdown.display')?.priceBreakdown?.display
    : maxBy(selectedSkus, 'price')?.price;
  const maxSkuPrice = isCAActMustBeApplied
    ? minBy(selectedSkus, 'priceBreakdown.display')?.priceBreakdown?.display
    : minBy(selectedSkus, 'price')?.price;

  const skusPriceLabel = getFormattedPrice({
    min: minSkuPrice,
    max: maxSkuPrice
  });

  const [images, setImages] = useState(product?.images);

  const hasSelectedSkus = selectedSkus?.length > 0;

  function getProductDetailStyle() {
    if (hasSelectedSkus) return { marginBottom: hideVerticalBasket ? 56 : 168 };
    return {};
  }

  return (
    <S.ProductDetailGeneralWrapper style={getProductDetailStyle()}>
      {isDraft && <TopNotifications someSkusUnavailable={someSkusUnavailable} product={product} />}
      <S.ProductDetailWrapper>
        <S.ProductDetailImageArea>
          <ProductDetailImage product={product} images={images} />
          <S.ProductDesc>
            <ShowMore numberOfLines={1}>
              <div {...getSafeInnerHtml(product.desc || '')} />
            </ShowMore>
          </S.ProductDesc>
        </S.ProductDetailImageArea>
        <S.ProductDetail>
          <ProductDetailInfo product={product} />
          {product?.orderable ? (
            <ProductOptionsByCheckbox
              product={product}
              setImages={setImages}
              selectedSkus={selectedSkus}
              setSelectedSkus={setSelectedSkus}
            />
          ) : (
            <S.ProductDetailNotification>
              <Notification
                status="warning"
                isClosable
                message={
                  <Typography variant="body2">
                    <Trans i18nKey="allVariantsUnavailable" />
                  </Typography>
                }
              />
            </S.ProductDetailNotification>
          )}
        </S.ProductDetail>
      </S.ProductDetailWrapper>

      {hasSelectedSkus && (
        <S.VerticalBasket>
          <S.VerticalBasketHeader>
            <S.VerticalBasketTitle>
              <Tooltip
                content={t('productHasMoreThanOneVariant')}
                tooltipConfig={{ placement: 'top-start' }}
              >
                <Icon name="tooltip" />
              </Tooltip>

              <Typography variant="body1SemiBold">
                {t('recipientCanChooseFrom')}:{' '}
                <Typography variant="body2" as="span" color="neutral" emphasis="500">
                  ({t(maxSkuPrice !== minSkuPrice ? 'itemPriceRange' : 'itemPrice')}:{' '}
                  <b style={{ fontWeight: 600, color: 'var(--color-neutral-800)' }}>
                    {skusPriceLabel}
                  </b>
                  )
                </Typography>
              </Typography>
            </S.VerticalBasketTitle>
            <Button
              variant="link"
              onClick={() => {
                setHideVerticalBasket(!hideVerticalBasket);
              }}
            >
              {t(!hideVerticalBasket ? 'hide' : 'show')}
            </Button>
          </S.VerticalBasketHeader>
          {!hideVerticalBasket && (
            <S.VerticalBasketBody>
              <Swiper
                slidesOffsetAfter={8}
                slidesOffsetBefore={8}
                navigation={{
                  prevEl: navigationPrevRef.current,
                  nextEl: navigationNextRef.current
                }}
                onBeforeInit={swiper => {
                  swiper.params.navigation.prevEl = navigationPrevRef.current;
                  swiper.params.navigation.nextEl = navigationNextRef.current;
                }}
                slidesPerView={'auto'}
                spaceBetween={16}
                modules={[Navigation]}
              >
                {selectedSkus?.map(sku => (
                  <SwiperSlide key={sku.code} style={{ maxWidth: 344 }}>
                    <HorizontalSkuCard
                      sku={sku}
                      onRemove={() => {
                        setSelectedSkus(
                          selectedSkus?.filter(selectedSku => selectedSku.code !== sku.code)
                        );
                      }}
                    />
                  </SwiperSlide>
                ))}
                <div style={{ visibility: selectedSkus?.length > 3 ? 'visible' : 'hidden' }}>
                  <S.SwiperPrevNavigation ref={navigationPrevRef}>
                    <S.IconWrapper>
                      <Icon name="next" px={9} color="var(--color-accent-secondary-default)" />
                    </S.IconWrapper>
                  </S.SwiperPrevNavigation>
                  <S.SwiperNextNavigation ref={navigationNextRef}>
                    <S.IconWrapper>
                      <Icon name="next" px={9} color="var(--color-accent-secondary-default)" />
                    </S.IconWrapper>
                  </S.SwiperNextNavigation>
                </div>
              </Swiper>
            </S.VerticalBasketBody>
          )}
        </S.VerticalBasket>
      )}
    </S.ProductDetailGeneralWrapper>
  );
}

ProductDetailByCheckbox.propTypes = {
  product: PropTypes.object,
  selectedSkus: PropTypes.array,
  setSelectedSkus: PropTypes.func,
  someSkusUnavailable: PropTypes.bool,
  isDraft: PropTypes.bool
};
